import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      toast.success(`Welcome, ${user.name}`);
      const roles = user?.roles || [user?.role];
      const hasAdminSide = roles.some((r) => ["super_admin", "director", "teacher", "receptionist", "accountant", "attendance_officer"].includes(r));
      navigate(roles.includes("parent") && !hasAdminSide ? "/parent" : "/app");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFBF7]" data-testid="login-page">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-[#FDFBF7] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/14921312/pexels-photo-14921312.jpeg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B2C5A]/95 via-[#1B2C5A]/85 to-[#14224A]/95" />
        <div className="relative z-10">
          <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-2 text-sm text-[#FDFBF7]/80 hover:text-[#9DCD3F] transition">
            <ArrowLeft className="w-4 h-4" /> Back to homepage
          </Link>
          <div className="mt-12">
            <div className="inline-flex items-center gap-3">
              <img src="/dfa-logo.svg" alt="DFA Logo" className="w-14 h-14 rounded-full bg-white p-1 ring-2 ring-[#9DCD3F]/40" />
              <div>
                <div className="font-display tracking-wide">DARUSSALAM</div>
                <div className="overline text-[#9DCD3F]">Foundation Academy</div>
              </div>
            </div>
            <h1 className="font-serif text-5xl mt-12 leading-[1.05]">
              The academy's<br /> nerve center.
            </h1>
            <p className="mt-6 max-w-md text-white/75 leading-relaxed">
              Sign in to manage students, attendance, fees, examinations, inventory and
              every facet of academy operations — securely from one cloud platform.
            </p>
            <p className="mt-6 text-[#9DCD3F] text-sm italic font-serif">Knowledge & Action</p>
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-3 text-sm text-[#FDFBF7]/70">
          <ShieldCheck className="w-4 h-4 text-[#9DCD3F]" />
          Secured with role-based access, audit logs and bcrypt-encrypted credentials.
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" data-testid="login-back-mobile" className="inline-flex items-center gap-2 text-sm text-[#475569]">
              <ArrowLeft className="w-4 h-4" /> Back home
            </Link>
          </div>
          <div className="overline text-[#9DCD3F]">Portal Sign In</div>
          <h2 className="font-serif text-4xl text-[#1B2C5A] mt-3">Welcome back.</h2>
          <p className="text-[#475569] mt-2">Enter your credentials to access your role-based portal.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#1B2C5A]">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@darussalam.edu"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#1B2C5A]"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#1B2C5A]">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#1B2C5A]"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-md bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]"
              data-testid="login-submit-button"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</> : "Sign in"}
            </Button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default Login;
